import { useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { bankApi } from '@/services/api'
import type { ParseCsvResponse, ValidateImportResponse, ImportMappedResponse } from '@/types'
import {
  Upload, FileText, CheckCircle, ArrowRight, ArrowLeft, Download,
  AlertTriangle, XCircle, Copy, Check, Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'

type WizardStep = 'upload' | 'map-columns' | 'date-format' | 'review' | 'complete'

const FIELD_DEFS = [
  { key: 'date', label: 'Transaction Date', required: true },
  { key: 'amount', label: 'Transaction Amount', required: true },
  { key: 'payee', label: 'Payee', required: false },
  { key: 'description', label: 'Description', required: false },
  { key: 'reference', label: 'Reference', required: false },
  { key: 'cheque_number', label: 'Cheque Number', required: false },
  { key: 'analysis_code', label: 'Analysis Code', required: false },
  { key: 'transaction_type', label: 'Transaction Type', required: false },
]

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'map-columns', label: 'Map Columns' },
  { key: 'date-format', label: 'Date Format' },
  { key: 'review', label: 'Review' },
  { key: 'complete', label: 'Complete' },
]

export default function ImportStatement() {
  const { clientId, bankAccountId } = useParams()
  const navigate = useNavigate()

  // Wizard state
  const [step, setStep] = useState<WizardStep>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)

  // Parse result
  const [parseResult, setParseResult] = useState<ParseCsvResponse | null>(null)

  // Column mapping
  const [mapping, setMapping] = useState<Record<string, string>>({})

  // Date format
  const [dateFormat, setDateFormat] = useState('')

  // Validation / review
  const [validation, setValidation] = useState<ValidateImportResponse | null>(null)
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set())

  // Import result
  const [importResult, setImportResult] = useState<ImportMappedResponse | null>(null)

  // --- Step 1: Upload ---
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const f = acceptedFiles[0]
      if (!f.name.toLowerCase().endsWith('.csv')) {
        toast.error('Only .csv files are accepted')
        return
      }
      setFile(f)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
  })

  async function handleUpload() {
    if (!file || !bankAccountId) return
    setLoading(true)
    try {
      const res = await bankApi.parseCsv(bankAccountId, file)
      const data: ParseCsvResponse = res.data
      if (data.error) {
        toast.error(data.error)
        return
      }
      setParseResult(data)

      // Pre-populate mapping from saved or auto-detected
      const initialMapping = data.saved_mapping && !data.headers_changed
        ? data.saved_mapping
        : data.auto_mapping
      setMapping(initialMapping)

      // Pre-populate date format
      if (data.saved_date_format && data.date_format_candidates.includes(data.saved_date_format)) {
        setDateFormat(data.saved_date_format)
      } else if (data.date_format_candidates.length === 1) {
        setDateFormat(data.date_format_candidates[0])
      }

      // If we have a saved mapping that matches, we can skip to date format or review
      if (data.saved_mapping && !data.headers_changed) {
        // Check if date format is unambiguous
        if (data.saved_date_format || data.date_format_candidates.length <= 1) {
          const df = data.saved_date_format || data.date_format_candidates[0] || 'YYYY-MM-DD'
          setDateFormat(df)
          setStep('map-columns') // Still show mapping for confirmation, user can skip
        } else {
          setStep('map-columns')
        }
      } else {
        setStep('map-columns')
      }
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      const message = typeof raw === 'string' ? raw
        : Array.isArray(raw) ? raw.map((e: { msg?: string }) => e.msg ?? String(e)).join('; ')
        : 'Failed to parse file'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  async function handleDownloadTemplate() {
    if (!bankAccountId) return
    try {
      const res = await bankApi.downloadTemplate(bankAccountId)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = 'bank-statement-template.csv'
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to download template')
    }
  }

  // --- Step 2: Map Columns ---
  function updateMapping(fieldKey: string, csvHeader: string) {
    setMapping((prev) => {
      const next = { ...prev }
      if (csvHeader === '') {
        delete next[fieldKey]
      } else {
        next[fieldKey] = csvHeader
      }
      return next
    })
  }

  const canProceedFromMapping = mapping['date'] && mapping['amount']

  function handleMappingNext() {
    if (!canProceedFromMapping) {
      toast.error('Date and Amount must be mapped')
      return
    }
    // Check if date format needs selection
    if (parseResult && parseResult.date_format_candidates.length > 1 && !dateFormat) {
      setStep('date-format')
    } else {
      if (!dateFormat && parseResult?.date_format_candidates.length === 1) {
        setDateFormat(parseResult.date_format_candidates[0])
      }
      handleValidate()
    }
  }

  // --- Step 3: Date Format ---
  function handleDateFormatNext() {
    if (!dateFormat) {
      toast.error('Please select a date format')
      return
    }
    handleValidate()
  }

  // --- Step 4: Validate / Review ---
  async function handleValidate() {
    if (!file || !bankAccountId) return
    setLoading(true)
    try {
      const res = await bankApi.validateImport(bankAccountId, file, mapping, dateFormat || 'YYYY-MM-DD')
      const data: ValidateImportResponse = res.data

      // Auto-exclude errors and duplicates
      const autoExclude = new Set<number>()
      for (const row of data.rows) {
        if (row.status === 'error' || row.status === 'duplicate') {
          autoExclude.add(row.row_index)
        }
      }
      setExcludedRows(autoExclude)
      setValidation(data)
      setStep('review')
    } catch (err: unknown) {

      const raw = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      const message = typeof raw === 'string' ? raw
        : Array.isArray(raw) ? raw.map((e: { msg?: string }) => e.msg ?? String(e)).join('; ')
        : 'Validation failed'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  function toggleRow(rowIndex: number) {
    setExcludedRows((prev) => {
      const next = new Set(prev)
      if (next.has(rowIndex)) {
        next.delete(rowIndex)
      } else {
        next.add(rowIndex)
      }
      return next
    })
  }

  // --- Step 5: Confirm Import ---
  async function handleConfirmImport() {
    if (!file || !bankAccountId) return
    setLoading(true)
    try {
      const res = await bankApi.importMapped(
        bankAccountId, file, mapping, dateFormat || 'YYYY-MM-DD', Array.from(excludedRows)
      )
      setImportResult(res.data)
      setStep('complete')
      toast.success(`${res.data.imported_count} transactions imported`)
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Import failed'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  // --- Stepper UI ---
  const currentStepIndex = STEPS.findIndex((s) => s.key === step)

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Import Bank Statement</h1>
        <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Upload and map your CSV bank statement</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 mb-6">
        {STEPS.map((s, i) => (
          <div key={s.key} className="flex items-center">
            <div className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors',
              i < currentStepIndex ? 'bg-emerald-50 text-emerald-700' :
              i === currentStepIndex ? 'bg-[#0078C8]/10 text-[#0078C8]' :
              'bg-[#E8E9EB] text-[rgba(0,10,30,0.75)]'
            )}>
              {i < currentStepIndex ? (
                <Check className="w-3 h-3" />
              ) : (
                <span className="w-4 h-4 rounded-full border flex items-center justify-center text-[10px]">
                  {i + 1}
                </span>
              )}
              {s.label}
            </div>
            {i < STEPS.length - 1 && (
              <div className={`w-6 h-px mx-1 ${i < currentStepIndex ? 'bg-emerald-300' : 'bg-[#CCCED2]'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      {step === 'upload' && (
        <div className="card p-6 space-y-5">
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all',
              isDragActive ? 'border-[#0078C8] bg-[#0078C8]/5' :
              file ? 'border-emerald-300 bg-emerald-50/30' :
              'border-[#CCCED2] hover:border-[#A6A9B0] hover:bg-surface-50'
            )}
          >
            <input {...getInputProps()} />
            {file ? (
              <div>
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-6 h-6 text-emerald-500" />
                </div>
                <p className="text-[13px] font-bold text-[#000A1E]">{file.name}</p>
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                <p className="text-[13px] text-[#0078C8] mt-2">Click or drag to replace</p>
              </div>
            ) : (
              <div>
                <div className="w-12 h-12 bg-[#E8E9EB] rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Upload className="w-6 h-6 text-[rgba(0,10,30,0.75)]" />
                </div>
                <p className="text-[13px] font-bold text-[#000A1E]">
                  {isDragActive ? 'Drop your file here' : 'Drop your CSV bank statement here'}
                </p>
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1">Only .csv files accepted (max 100,000 rows)</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <button onClick={handleDownloadTemplate} className="btn-ghost text-xs">
              <Download className="w-3.5 h-3.5" /> Download CSV template
            </button>
            <div className="flex gap-2">
              <button onClick={() => navigate(`/clients/${clientId}/banking`)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleUpload} disabled={!file || loading} className="btn-primary">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing...</> : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'map-columns' && parseResult && (
        <div className="space-y-4">
          <div className="card p-5">
            <h3 className="text-[13px] font-bold text-[#000A1E] mb-1">Assign columns</h3>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-4">
              Map each CSV column to the corresponding field. Date and Amount are required.
              {parseResult.cleaned_columns.length > 0 && (
                <span className="text-amber-600 ml-1">
                  Balance columns detected and will be ignored: {parseResult.cleaned_columns.join(', ')}
                </span>
              )}
            </p>

            <div className="space-y-2.5">
              {FIELD_DEFS.map((field) => (
                <div key={field.key} className="flex items-center gap-3">
                  <div className="w-44 flex-shrink-0">
                    <span className="text-[13px] text-[#000A1E]">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-0.5">*</span>}
                    </span>
                  </div>
                  <select
                    value={mapping[field.key] || ''}
                    onChange={(e) => updateMapping(field.key, e.target.value)}
                    className="input flex-1"
                  >
                    <option value="">— Skip —</option>
                    {parseResult.headers
                      .filter((h) => !parseResult.cleaned_columns.includes(h))
                      .map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))
                    }
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Preview table */}
          <div className="card overflow-hidden">
            <div className="px-4 py-2.5 border-b border-surface-200 bg-surface-50">
              <p className="text-[13px] font-bold text-[#000A1E]">
                Preview <span className="text-[rgba(0,10,30,0.75)] font-normal">({parseResult.total_rows} rows total, showing first 5)</span>
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-surface-200 bg-surface-50/50">
                    {parseResult.headers.map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-bold text-[#000A1E] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100">
                  {parseResult.preview_rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} className="px-3 py-2 text-[#000A1E] whitespace-nowrap max-w-[200px] truncate">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => { setStep('upload'); setParseResult(null) }} className="btn-secondary">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={handleMappingNext} disabled={!canProceedFromMapping || loading} className="btn-primary">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Validating...</> : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      )}

      {step === 'date-format' && parseResult && (
        <div className="space-y-4">
          <div className="card p-5">
            <h3 className="text-[13px] font-bold text-[#000A1E] mb-1">Select date format</h3>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-4">
              The dates in your file could match multiple formats. Please confirm which one is correct.
            </p>

            {/* Show a sample date in each format */}
            <div className="space-y-2">
              {parseResult.date_format_candidates.map((fmt) => {
                // Show the first preview date value
                const dateCol = mapping['date']
                let sampleRaw = ''
                if (dateCol && parseResult.headers.includes(dateCol)) {
                  const idx = parseResult.headers.indexOf(dateCol)
                  sampleRaw = parseResult.preview_rows[0]?.[idx] || ''
                }

                return (
                  <label
                    key={fmt}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                      dateFormat === fmt
                        ? 'border-[#0078C8] bg-[#0078C8]/5'
                        : 'border-[#CCCED2] hover:border-[#A6A9B0]'
                    )}
                  >
                    <input
                      type="radio"
                      name="date_format"
                      value={fmt}
                      checked={dateFormat === fmt}
                      onChange={() => setDateFormat(fmt)}
                      className="text-[#0078C8] focus:ring-[#0078C8]/20"
                    />
                    <div>
                      <span className="text-[13px] font-bold text-[#000A1E] font-mono">{fmt}</span>
                      {sampleRaw && (
                        <span className="text-[13px] text-[rgba(0,10,30,0.75)] ml-2">
                          e.g. "{sampleRaw}"
                        </span>
                      )}
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep('map-columns')} className="btn-secondary">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={handleDateFormatNext} disabled={!dateFormat || loading} className="btn-primary">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Validating...</> : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      )}

      {step === 'review' && validation && (
        <div className="space-y-4">
          {/* Summary stats */}
          <div className="grid grid-cols-5 gap-3">
            {[
              { label: 'Total', value: validation.total, cls: 'text-[#000A1E]' },
              { label: 'Ready', value: validation.valid, cls: 'text-emerald-600' },
              { label: 'Warnings', value: validation.warnings, cls: 'text-amber-600' },
              { label: 'Errors', value: validation.errors, cls: 'text-red-600' },
              { label: 'Duplicates', value: validation.duplicates, cls: 'text-[#CCCED2]' },
            ].map((s) => (
              <div key={s.label} className="card px-4 py-3">
                <p className={`text-xl font-bold ${s.cls}`}>{s.value}</p>
                <p className="text-[11px] text-[rgba(0,10,30,0.75)] mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Transactions table */}
          <div className="card overflow-hidden">
            <div className="px-4 py-2.5 border-b border-surface-200 bg-surface-50 flex items-center justify-between">
              <p className="text-[13px] font-bold text-[#000A1E]">Transactions to review</p>
              <p className="text-[13px] text-[rgba(0,10,30,0.75)]">
                {validation.rows.length - excludedRows.size} selected for import
              </p>
            </div>
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-surface-50 z-10">
                  <tr className="border-b border-surface-200">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2 text-left font-bold text-[#000A1E]">#</th>
                    <th className="px-3 py-2 text-left font-bold text-[#000A1E]">Date</th>
                    <th className="px-3 py-2 text-right font-bold text-[#000A1E]">Amount</th>
                    <th className="px-3 py-2 text-left font-bold text-[#000A1E]">Description</th>
                    <th className="px-3 py-2 text-left font-bold text-[#000A1E]">Payee</th>
                    <th className="px-3 py-2 text-left font-bold text-[#000A1E]">Status</th>
                    <th className="px-3 py-2 text-left font-bold text-[#000A1E]">Messages</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-50">
                  {validation.rows.map((row) => {
                    const isExcluded = excludedRows.has(row.row_index)
                    const isError = row.status === 'error'
                    return (
                      <tr
                        key={row.row_index}
                        className={cn(
                          'transition-colors',
                          isExcluded ? 'opacity-40 bg-surface-50' : 'hover:bg-surface-50'
                        )}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            disabled={isError}
                            onChange={() => toggleRow(row.row_index)}
                            className="rounded border-[#CCCED2] text-[#0078C8] focus:ring-[#0078C8]/20"
                          />
                        </td>
                        <td className="px-3 py-2 text-[rgba(0,10,30,0.75)]">{row.row_index + 1}</td>
                        <td className="px-3 py-2 text-[#000A1E] whitespace-nowrap">{row.date}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-[#000A1E] whitespace-nowrap">
                          {row.amount}
                        </td>
                        <td className="px-3 py-2 text-[#000A1E] max-w-[200px] truncate">{row.description}</td>
                        <td className="px-3 py-2 text-[#000A1E] max-w-[120px] truncate">{row.payee}</td>
                        <td className="px-3 py-2">
                          <span className={
                            row.status === 'ok' ? 'badge badge-green' :
                            row.status === 'warning' ? 'badge badge-yellow' :
                            row.status === 'duplicate' ? 'badge badge-blue' :
                            'badge badge-red'
                          }>
                            {row.status === 'ok' ? <CheckCircle className="w-3 h-3 mr-0.5" /> :
                             row.status === 'warning' ? <AlertTriangle className="w-3 h-3 mr-0.5" /> :
                             row.status === 'duplicate' ? <Copy className="w-3 h-3 mr-0.5" /> :
                             <XCircle className="w-3 h-3 mr-0.5" />}
                            {row.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-[rgba(0,10,30,0.75)] text-[11px] max-w-[200px] truncate">
                          {row.messages.join('; ')}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep('map-columns')} className="btn-secondary">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button
              onClick={handleConfirmImport}
              disabled={loading || (validation.rows.length - excludedRows.size) === 0}
              className="btn-primary"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Importing...</>
              ) : (
                <>Import {validation.rows.length - excludedRows.size} transactions</>
              )}
            </button>
          </div>
        </div>
      )}

      {step === 'complete' && importResult && (
        <div className="card p-10 text-center">
          <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-7 h-7 text-emerald-500" />
          </div>
          <h2 className="text-[21px] font-bold text-[#000A1E] leading-[32px] mb-2">Import Complete</h2>
          <div className="text-[13px] text-[rgba(0,10,30,0.75)] space-y-1 mb-8">
            <p><span className="font-bold text-[#000A1E]">{importResult.imported_count}</span> transactions imported</p>
            {importResult.duplicate_count > 0 && (
              <p><span className="font-bold text-amber-600">{importResult.duplicate_count}</span> duplicates skipped</p>
            )}
            {importResult.error_count > 0 && (
              <p><span className="font-bold text-red-600">{importResult.error_count}</span> rows had errors</p>
            )}
          </div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate(`/clients/${clientId}/banking/${bankAccountId}/reconcile`)}
              className="btn-primary"
            >
              Start Reconciling <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setFile(null)
                setParseResult(null)
                setMapping({})
                setDateFormat('')
                setValidation(null)
                setExcludedRows(new Set())
                setImportResult(null)
                setStep('upload')
              }}
              className="btn-secondary"
            >
              Import Another
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
